// アプリのシェル: 上部バー + タブごとのコンテンツ + 下部タブバー。

import { store, type TabId } from "../store/state.ts";
import { renderAvatarView, handleFile } from "./avatarView.ts";
import { renderExpressionView } from "./expressionView.ts";
import { renderGestureView } from "./gestureView.ts";
import { renderExportView } from "./exportView.ts";
import { clear, el, icon } from "./dom.ts";

const TABS: Array<{ id: TabId; label: string; icon: Parameters<typeof icon>[0]; needsAvatar: boolean }> = [
  { id: "avatar", label: "アバター", icon: "avatar", needsAvatar: false },
  { id: "expression", label: "表情", icon: "expression", needsAvatar: true },
  { id: "gesture", label: "ジェスチャー", icon: "gesture", needsAvatar: true },
  { id: "export", label: "書き出し", icon: "export", needsAvatar: true },
];

function banner(): HTMLElement | null {
  const b = store.banner;
  if (!b) return null;
  return el(
    "div",
    { class: `banner${b.kind === "info" ? " info" : ""}` },
    el(
      "div",
      { class: "row" },
      el("h3", { text: b.title, style: { flex: "1 1 auto" } }),
      el("button", { class: "icon-btn", text: "✕", "aria-label": "閉じる", onclick: () => store.setBanner(null) }),
    ),
    el("p", { text: b.detail }),
    b.hint ? el("p", { class: "hint", text: b.hint }) : null,
  );
}

function content(): HTMLElement {
  switch (store.tab) {
    case "avatar":
      return renderAvatarView();
    case "expression":
      return renderExpressionView();
    case "gesture":
      return renderGestureView();
    case "export":
      return renderExportView();
    default:
      return el("div", {});
  }
}

function tabbar(): HTMLElement {
  const bar = el("nav", { class: "tabbar", role: "tablist" });
  for (const t of TABS) {
    const disabled = t.needsAvatar && !store.avatar;
    bar.appendChild(
      el(
        "button",
        {
          role: "tab",
          "aria-selected": store.tab === t.id ? "true" : "false",
          disabled,
          onclick: () => store.setTab(t.id),
        },
        el("span", { class: "ico" }, icon(t.icon)),
        el("span", { text: t.label }),
      ),
    );
  }
  return bar;
}

export function mountApp(root: HTMLElement): void {
  const shell = el("div", {
    class: "shell",
    ondragover: (e: DragEvent) => {
      if (store.tab !== "avatar") return;
      e.preventDefault();
      shell.querySelector(".dropzone")?.classList.add("over");
    },
    ondragleave: (e: DragEvent) => {
      if (store.tab !== "avatar") return;
      if (e.relatedTarget instanceof Node && shell.contains(e.relatedTarget)) return;
      shell.querySelector(".dropzone")?.classList.remove("over");
    },
    ondrop: (e: DragEvent) => {
      if (store.tab !== "avatar") return;
      e.preventDefault();
      shell.querySelector(".dropzone")?.classList.remove("over");
      const f = e.dataTransfer?.files?.[0];
      if (f) void handleFile(f);
    },
  });
  const top = el(
    "div",
    { class: "topbar" },
    el(
      "div",
      { class: "brand" },
      el("img", { class: "brand-icon", src: "/icon-96.png", alt: "" }),
      el("span", { class: "brand-text", text: "FaceMaker" }),
    ),
    el("span", { class: "avatar-name" }),
  );
  const contentHost = el("div", { class: "content" });
  const tabHost = el("div", { style: { display: "contents" } });

  shell.append(top, contentHost, tabHost);
  clear(root).appendChild(shell);

  const render = (): void => {
    const nameEl = top.querySelector<HTMLElement>(".avatar-name");
    if (nameEl) nameEl.textContent = store.avatar?.manifest.avatarName ?? "";

    clear(contentHost);
    const b = banner();
    if (b) contentHost.appendChild(b);
    contentHost.appendChild(content());

    clear(tabHost).appendChild(tabbar());
  };

  store.subscribe(render);
  render();
}
